import React, { useCallback, useEffect } from "react";
import { Grid } from "@mui/material";
import { FormField, RadioButtonGroup } from "../shared/form";
import { MisdeliveryFormData } from "../../models/misDelivery";
import Location from "./../../screens/overageEntry/Location";
import { mapDestinationToPickupTerminal } from "../../helpers";
import {
  CURRENT_LOCATION_FORM_OBJECT,
  LOCATION_FORM_OBJECT,
  M_GRID_PROPS,
  S_GRID_PROPS,
} from "../../constants";
import { useMisdeliveryFormContext } from "../../contexts/MisdeliveryFormContext";
import NoteBox from "../noteBox/NoteBox";

interface MisdeliveryInformationSectionProps {
  osdDetails?: any;
}

// Helper: Check if field has error
const hasFieldError = (
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  fieldName: string,
): boolean => {
  return !!(touched[fieldName] && errors[fieldName]);
};

// Helper: Get field helper text
const getFieldHelperText = (
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  fieldName: string,
): string | undefined => {
  return touched[fieldName] ? errors[fieldName] : undefined;
};

// Helper: Check if value is "SAME" (case-insensitive)
const isSameValue = (value: string | undefined | null): boolean => {
  return value?.trim().toUpperCase() === "SAME";
};

const MisdeliveryInformationSection: React.FC<
  MisdeliveryInformationSectionProps
> = ({ osdDetails }) => {
  // Get form state from context
  const {
    formData,
    errors,
    touched,
    onFieldChange,
    handleChange,
    handleBlur,
    disabled,
    setFieldTouched,
  } = useMisdeliveryFormContext();

  const clearCurrentLocationTouched = useCallback(() => {
    const fieldsToClear: (keyof MisdeliveryFormData)[] = [
      "currentCompanyName",
      "currentLocationAddress1",
      "currentLocationAddress2",
      "currentLocationCity",
      "currentLocationZip",
      "currentLocationState",
      "pickupTerminal",
    ];

    fieldsToClear.forEach((field) => {
      // Clear touched so any existing validation messages hide immediately
      // when values are auto-populated or reset.
      setFieldTouched(field, false, false);
    });
  }, [setFieldTouched]);

  const clearConsigneeTouchedForPro = useCallback(() => {
    const fieldsToClear: (keyof MisdeliveryFormData)[] = [
      "consigneeName",
      "consigneeAddress1",
      "consigneeCity",
      "consigneeZip",
    ];

    fieldsToClear.forEach((field) => {
      setFieldTouched(field, false, false);
    });
  }, [setFieldTouched]);

  useEffect(() => {
    if (formData.hasProNumber === "Y") {
      clearConsigneeTouchedForPro();
    }
  }, [formData.hasProNumber, clearConsigneeTouchedForPro]);

  const resetCurrentLocationFields = useCallback(() => {
    const currentAddressFields: (keyof MisdeliveryFormData)[] = [
      "currentCompanyName",
      "currentLocationAddress1",
      "currentLocationAddress2",
      "currentLocationCity",
      "currentLocationZip",
      "currentLocationState",
      "pickupTerminal",
    ];
    currentAddressFields.forEach((field) => {
      onFieldChange(field as keyof MisdeliveryFormData, ""); // NOSONAR
    });
    clearCurrentLocationTouched();
  }, [onFieldChange, clearCurrentLocationTouched]);

  const populateCurrentLocationFromMisdelivery = useCallback(() => {
    onFieldChange("currentLocationAddress1", formData?.consigneeAddress1);
    onFieldChange("currentCompanyName", formData?.consigneeName);
    onFieldChange("currentLocationAddress2", formData?.consigneeAddress2);
    onFieldChange("currentLocationCity", formData?.consigneeCity);
    onFieldChange("currentLocationState", formData?.consigneeState);
    onFieldChange("currentLocationZip", formData?.consigneeZip);
    onFieldChange("pickupTerminal", mapDestinationToPickupTerminal(formData?.destinationTerminal));
    clearCurrentLocationTouched();
  }, [onFieldChange, formData, clearCurrentLocationTouched]);

  // Helper to clear touched state for current contact fields
  const clearCurrentContactTouched = useCallback(() => {
    const fieldsToClear: (keyof MisdeliveryFormData)[] = [
      "currentContactPhone",
      "currentContactExt",
      "currentContactEmail",
    ];
    fieldsToClear.forEach((field) => {
      setFieldTouched(field, false, false);
    });
  }, [setFieldTouched]);

  // Check if currentContactName is "SAME" (case-insensitive)
  const isSameContactName = isSameValue(formData.currentContactName);

  // Synchronize current contact details from caller info when "SAME" is entered
  useEffect(() => {
    if (isSameContactName) {
      let changed = false;
      if (formData.currentContactPhone !== formData.callerPhone) {
        onFieldChange("currentContactPhone", formData.callerPhone);
        changed = true;
      }
      if (formData.currentContactExt !== formData.callerExt) {
        onFieldChange("currentContactExt", formData.callerExt);
        changed = true;
      }
      if (formData.currentContactEmail !== formData.callerEmail) {
        onFieldChange("currentContactEmail", formData.callerEmail);
        changed = true;
      }
      if (changed) {
        clearCurrentContactTouched();
      }
    }
  }, [
    isSameContactName,
    formData.callerPhone,
    formData.callerExt,
    formData.callerEmail,
    formData.currentContactPhone,
    formData.currentContactExt,
    formData.currentContactEmail,
    onFieldChange,
    clearCurrentContactTouched
  ]);

  // Handle currentContactName field change
  const handleCurrentContactNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e);
    },
    [handleChange],
  );

  // Field change handler for RadioButtonGroup
  const handleFieldChange = useCallback(
    (fieldName: keyof MisdeliveryFormData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value;
        onFieldChange(fieldName, nextValue);

        if (fieldName !== "currentLocationDifferentThanMsdPoint") return;

        if (nextValue === "Y") {
          resetCurrentLocationFields();
        } else if (nextValue === "N") {
          populateCurrentLocationFromMisdelivery();
        }
      },
    [
      onFieldChange,
      resetCurrentLocationFields,
      populateCurrentLocationFromMisdelivery,
    ],
  );

  const hasProNumber = formData.hasProNumber === "Y";
  const isHasProSelected = ["Y", "N"].includes(formData.hasProNumber ?? "");
  const isMisdeliveryFieldsDisabled =
    !isHasProSelected || hasProNumber || disabled;

  // Helper to render FormField with common props
  const renderFormField = useCallback(
    (
      id: keyof MisdeliveryFormData,
      label: string,
      placeholder: string,
      options: {
        maxLength?: number;
        required?: boolean;
        type?: "number" | "text" | "email";
        disabled?: boolean;
        gridProps?: any;
        error?: boolean;
        helperText?: string;
      } = {},
    ) => {
      const value = formData[id] ?? "";
      const errorMsg = errors[id];
      const isRequiredError = errorMsg?.includes("Required") && formData[id];
      const showError = touched[id] && errorMsg && !isRequiredError;

      return (
        <FormField
          id={id}
          label={label}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={options.maxLength}
          required={options.required}
          type={options.type}
          disabled={options.disabled}
          error={options.error ?? !!showError}
          helperText={options.helperText ?? (showError ? errorMsg : undefined)}
          gridProps={options.gridProps}
        />
      );
    },
    [formData, touched, errors, handleChange, handleBlur],
  );

  const memoizedFormik = React.useMemo(
    () => ({
      handleBlur,
      setFieldValue: onFieldChange,
      errors,
      touched,
      values: formData,
      setFieldTouched,
    }),
    [handleBlur, onFieldChange, errors, touched, formData, setFieldTouched],
  );

  const isContactInfoTouched =
    (touched.callerPhone || touched.callerEmail) ?? false; /* NOSONAR */

  const isContactInfoEmpty = !formData.callerEmail && !formData.callerPhone;

  const contactInfoError =
    isContactInfoTouched && isContactInfoEmpty
      ? "At least one of Phone #, or Email is required"
      : undefined;

  const isCurrentContactInfoTouched =
    touched.currentContactPhone || touched.currentContactEmail;

  const isCurrentContactInfoEmpty = !formData.currentContactEmail && !formData.currentContactPhone;

  const currentContactInfoError =
    isCurrentContactInfoTouched && isCurrentContactInfoEmpty
      ? "At least one of Phone #, or Email is required"
      : undefined;

  const lockCurrentLocationFields =
    formData.currentLocationDifferentThanMsdPoint === "N";

  return (
    <>
      <h3 className="accordion-content-header">Caller Information</h3>
      <Grid container spacing={2} className="filter-margin">
        {renderFormField("callerName", "Caller Name", "Enter Name", {
          maxLength: 30,
          required: true,
          gridProps: S_GRID_PROPS,
          disabled,
        })}
        {renderFormField("callerCompanyName", "Company Name", "Enter Company Name", {
          maxLength: 30,
          required: true,
          gridProps: S_GRID_PROPS,
          disabled,
        })}

        {renderFormField("callerPhone", "Phone #", "Enter Phone #", {
          maxLength: 10,
          required: true,
          type: "number",
          gridProps: { md: 2, xs: 12 },
          error: !!contactInfoError  || !!errors["callerPhone"],
          helperText: contactInfoError || errors["callerPhone"],
          disabled,
        })}
        {renderFormField("callerExt", "Ext", "Enter Ext", {
          maxLength: 6,
          type: "number",
          required: false,
          disabled,
          gridProps: { md: 1, xs: 12 },
        })}

        {renderFormField("callerEmail", "Email", "Enter Email Address", {
          maxLength: 40,
          required: true,
          gridProps: S_GRID_PROPS,
          error:
            !!contactInfoError || !!(touched.callerEmail && errors.callerEmail),
          helperText: contactInfoError,
          disabled,
        })}
      </Grid>
      <h3 className="accordion-content-header">Consignee Information</h3>

      <Grid container spacing={2} className="filter-margin">
        {renderFormField(
          "consigneeName",
          "Company Name",
          "Enter Company Name",
          {
            maxLength: 30,
            required: true,
            disabled: isMisdeliveryFieldsDisabled,
            gridProps: S_GRID_PROPS,
          },
        )}
        {renderFormField(
          "consigneeAddress1",
          "Address 1",
          "Enter Address 1",
          {
            maxLength: 30,
            required: true,
            disabled: isMisdeliveryFieldsDisabled,
            gridProps: S_GRID_PROPS,
          },
        )}

        {renderFormField(
          "consigneeAddress2",
          "Address 2",
          "Enter Address 2",
          {
            maxLength: 30,
            disabled: isMisdeliveryFieldsDisabled,
            gridProps: S_GRID_PROPS,
          },
        )}

        {hasProNumber && (
          <>
            {renderFormField("consigneeCity", "City", "City", {
              maxLength: 20,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
            {renderFormField("consigneeState", "State", "State", {
              maxLength: 2,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
            {renderFormField("consigneeZip", "Zipcode", "Zipcode", {
              maxLength: 6,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
             {renderFormField("consigneeAccount", "Account #", "Account #", {
              maxLength: 6,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
          </>
        )}
        {formData.hasProNumber !== "Y" && (
          <Location
            key={formData.consigneeState} // Force remount when hasProNumber changes to reset internal state
            osdDetails={osdDetails}
            formFieldsDisabled={isMisdeliveryFieldsDisabled}
            formik={memoizedFormik}
            formObject={LOCATION_FORM_OBJECT}
            required={true}
          />
        )}

        {hasProNumber && (
          <>
            <Grid item {...S_GRID_PROPS}></Grid>
            <RadioButtonGroup
              id="currentLocationDifferentThanMsdPoint"
              label="Is the current location of freight different than consignee location?"
              value={formData.currentLocationDifferentThanMsdPoint}
              onChange={handleFieldChange(
                "currentLocationDifferentThanMsdPoint",
              )}
              onBlur={handleBlur}
              required
              error={hasFieldError(
                touched,
                errors,
                "currentLocationDifferentThanMsdPoint",
              )}
              helperText={getFieldHelperText(
                touched,
                errors,
                "currentLocationDifferentThanMsdPoint",
              )}
              gridProps={S_GRID_PROPS}
              disabled={disabled}
            />
            {renderFormField(
              "currentCompanyName",
              "Company Name",
              "Enter  Company Name",
              {
                maxLength: 30,
                required: true,
                gridProps: S_GRID_PROPS,
                disabled: disabled || lockCurrentLocationFields,
              },
            )}
            {renderFormField(
              "currentLocationAddress1",
              "Address 1",
              "Enter  Address  1",
              {
                maxLength: 30,
                required: true,
                gridProps: S_GRID_PROPS,
                disabled: disabled || lockCurrentLocationFields,
              },
            )}
            {renderFormField(
              "currentLocationAddress2",
              "Address 2",
              "Enter  Address  2",
              {
                maxLength: 30,
                gridProps: S_GRID_PROPS,
                disabled: disabled || lockCurrentLocationFields,
              },
            )}
            <Location
              osdDetails={osdDetails}
              key={formData.currentLocationState}
              formFieldsDisabled={disabled || lockCurrentLocationFields}
              formik={memoizedFormik}
              formObject={CURRENT_LOCATION_FORM_OBJECT}
              required={true}
            />
          </>
        )}
        </Grid>
        {hasProNumber && (
        <Grid container spacing={2} className="filter-margin">
            <FormField
              id="currentContactName"
              label="Contact name of current location of freight"
              value={formData.currentContactName ?? ""}
              onChange={handleCurrentContactNameChange}
              onBlur={handleBlur}
              placeholder="Enter current contact name at MSD location of freight"
              maxLength={30}
              required
              disabled={disabled}
              error={!!(touched.currentContactName && errors.currentContactName)}
              helperText={touched.currentContactName ? errors.currentContactName : undefined}
              gridProps={M_GRID_PROPS}
            />
            {renderFormField(
              "currentContactPhone",
              "Phone #",
              "Enter Phone #",
              {
                maxLength: 10,
                type: "number",
                required: true,
                gridProps: {md:2, xs:12},
                error: !!currentContactInfoError || !!errors["currentContactPhone"],
                helperText: currentContactInfoError || errors["currentContactPhone"],
                disabled: disabled || isSameContactName,
              },
            )}
            {renderFormField("currentContactExt", " Ext", "Enter Ext", {
              maxLength: 6,
              type: "number",
               gridProps: {md:1, xs:12},
              disabled: disabled || isSameContactName,
            })}

            {renderFormField(
              "currentContactEmail",
              "Email Address",
              "Enter Email Address",
              {
                maxLength: 40,
                required: true,
                gridProps: S_GRID_PROPS,
                error: !!currentContactInfoError || !!(touched.currentContactEmail && errors.currentContactEmail),
                helperText: currentContactInfoError,
                disabled: disabled || isSameContactName,
              },
            )}
        </Grid>
        )}
        <Grid container spacing={2} className="filter-margin">
        <RadioButtonGroup
          id="shipmentDeliveredOutsideUS"
          label="Was shipment delivered outside US?"
          value={formData.shipmentDeliveredOutsideUS}
          onChange={handleFieldChange("shipmentDeliveredOutsideUS")}
          onBlur={handleBlur}
          required
          error={hasFieldError(touched, errors, "shipmentDeliveredOutsideUS")}
          helperText={getFieldHelperText(
            touched,
            errors,
            "shipmentDeliveredOutsideUS",
          )}
          gridProps={M_GRID_PROPS}
          disabled={disabled}
        />
        {formData.shipmentDeliveredOutsideUS === "Y" && (
          <> 
          <Grid item {...M_GRID_PROPS} className="mt-10"> </Grid>
          <Grid item {...M_GRID_PROPS} className="mt-10">
            <NoteBox message="OSDCLP@SAIA.COM approval required before terminal can schedule an MSD pickup." />
          </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default MisdeliveryInformationSection;
